import React, { useState, useEffect } from "react";
import Pagination from "../../common/Pagination";
import ConfirmModal from "../../common/ConfirmModal";
import Tooltip from "../../common/Tooltip";
import taskService from "../../../services/taskService";
import TaskModal from "../TaskModal";
import { useNotification } from "../../../contexts/NotificationContext";
import debounce from "lodash/debounce";
import { useLoader } from "../../../contexts/LoaderContext";
import { useAuth } from "../../../contexts/AuthContext";
import { Eye, Edit, Trash2 } from "lucide-react";

const headers = [
	{
		name: "Task Name",
		value: "name",
		styles: "min-w-[280px]",
		sortable: true,
	},
	{
		name: "Assign By",
		value: "assignedByUser",
		styles: "min-w-[190px]",
		sortable: true,
	},
	{
		name: "Assign to",
		value: "assignedToUser",
		styles: "min-w-[190px]",
		sortable: true,
	},
	{
		name: "Due date",
		value: "dueDate",
		styles: "min-w-[190px]",
		sortable: true,
	},

	{
		name: "Status",
		value: "status",
		styles: "min-w-[180px]",
		sortable: true,
	},
	{
		name: "Action",
		styles: "min-w-[180px]",
		sortable: false,
	},
];

const pageSizeOptions = [1, 10, 25, 50, 100];

const TaskBoard = () => {
	const { taskList, getTaskStatuses, deleteTask } = taskService();
	const [tasks, setTasks] = useState([]);
	const [error, setError] = useState("");
	const [sortBy, setSortBy] = useState(headers[3]?.value);
	const [sortOrder, setSortOrder] = useState("desc");
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [totalPages, setTotalPages] = useState(1);
	const [statuses, setStatuses] = useState([]);
	const [modalStatuses, setModalStatuses] = useState([]);
	const [filters, setFilters] = useState({
		taskName: "",
		assignedByUser: "",
		assignedToUser: "",
		dueDate: "",
		status: "",
		quickFilter: "",
	});
	const [showMobileFilters, setShowMobileFilters] = useState(false);
	const filtersApplied = Object.values(filters).some(
		(v) => v !== null && v !== undefined && String(v).trim() !== ""
	);
	const resetFilters = () => {
		setFilters({
			taskName: "",
			assignedByUser: "",
			assignedToUser: "",
			dueDate: "", //new Date().toISOString().split("T")[0],
			status: "",
			quickFilter: "",
		});
	};
	const [confirmModal, setConfirmModal] = useState({
		isOpen: false,
		data: {},
		headerText: "Delete Task",
		bodyText: "Are you sure you want to Delete Task?",
		bodySubText:
			"Please note that this process won’t be reversible and all your task data will be deleted.",
		btnConfirmText: "Confirm",
		btnCancelText: "Cancel",
	});

	const [taskModal, setTaskModal] = useState({
		isTaskModalOpen: false,
		headerText: "",
		mode: "",
		data: {},
		taskStatuses: [],
	});

	const { showNotification } = useNotification();
	const { showLoader, hideLoader } = useLoader();
	const { user } = useAuth();
	const [loggedInUserName, setLoggedInUserName] = useState("");
	const [loggedInUserId, setLoggedInUserId] = useState("");
	const [isLoggedInUserSuperAdmin, setIsLoggedInUserSuperAdmin] = useState("");

	const handleSortByChange = (event) => {
		setSortBy(event.target.value);
	};

	const toggleSortOrder = () => {
		setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
	};

	const handleFilterChange = (eOrObject) => {
		if (eOrObject.target) {
			const { name, value } = eOrObject.target;
			setFilters((prev) => ({ ...prev, [name]: value }));
		} else if (typeof eOrObject === "object" && eOrObject !== null) {
			setFilters((prev) => ({ ...prev, ...eOrObject }));
		}
	};

	const handlePageChange = (newPage) => {
		setPage(newPage);
	};

	const handlePageSizeChange = (e) => {
		setPageSize(Number(e.target.value));
		setPage(0);
	};

	const createTaskHandler = () => {
		setTaskModal((prev) => ({
			...prev,
			isTaskModalOpen: true,
			headerText: "Create New Task",
			mode: "create",
			data: {},
			taskStatuses: modalStatuses,
		}));
	};

	const editTaskHandler = (taskId) => {
		setTaskModal((prev) => ({
			...prev,
			isTaskModalOpen: true,
			headerText: "Edit Task",
			mode: "edit",
			data: { taskId: taskId },
			taskStatuses: modalStatuses,
		}));
	};

	const viewTaskHandler = (taskId) => {
		setTaskModal((prev) => ({
			...prev,
			isTaskModalOpen: true,
			headerText: "View Task",
			mode: "view",
			data: { taskId: taskId },
			taskStatuses: modalStatuses,
		}));
	};

	const onTaskCancel = () => {
		setTaskModal((prev) => ({
			...prev,
			isTaskModalOpen: false,
			headerText: "",
			data: {},
		}));
	};

	const onTaskRefresh = () => {
		fetchTasks();
	};

	const deleteTaskHandler = (taskId) => {
		setConfirmModal((prev) => ({ ...prev, isOpen: true, data: { taskId: taskId } }));
	};


	const [isDeleting, setIsDeleting] = useState(false);

	const confirmDelete = async () => {
		try {
			if (isDeleting) return; // prevent second click
			setIsDeleting(true);
			showLoader();
			if (confirmModal.data && confirmModal.data.taskId) {
				const response = await deleteTask(confirmModal.data);
				showNotification(`Task deleted successfully!`, "success");
				debouncedFetchTasks();
			}
		} catch (error) {
			showNotification(`Error in deleting task: ${error.message}`, "error");
		} finally {
			setIsDeleting(false);
			hideLoader();
			setConfirmModal((prev) => ({ ...prev, isOpen: false, data: {} }));

		}
	};

	const cancelDelete = () => {
		setConfirmModal((prev) => ({ ...prev, isOpen: false, data: {} }));
	};

	const fetchTaskStatuses = async () => {
		try {
			const response = await getTaskStatuses();
			const formattedStatuses = response.map((status) => ({
				name: status.displayName,
				value: status.name,
			}));
			setModalStatuses(formattedStatuses);
			setStatuses([{ name: "All", value: "" }, ...formattedStatuses]);
		} catch (error) {
			console.error("Error fetching statuses:", error);
		}
	};
	const fetchTasks = async () => {
		try {
			const response = await taskList({
				page: page,
				size: pageSize,
				sortBy,
				sortOrder,
				filters: {
					taskName: filters.taskName || "",
					assignedByUser: filters.assignedByUser || "",
					assignedToUser: filters.assignedToUser || "",
					dueDate: filters.dueDate || "",
					status: filters.status || "",
				},
			});
			setTasks(response.data.content);
			setTotalPages(response.data.totalPages);
		} catch (err) {
			setError(err?.message || "Error fetching tasks");
		}
	};

	useEffect(() => {
		fetchTaskStatuses();
		if (user) {
			setLoggedInUserName(`${user.firstName || ""} ${user.lastName || ""}`);
			setLoggedInUserId(user.id || "");
			setIsLoggedInUserSuperAdmin(user.role == "SUPER_ADMIN");
		}
	}, []);

	const debouncedFetchTasks = debounce(fetchTasks, 500);

	useEffect(() => {
		debouncedFetchTasks();
		return () => debouncedFetchTasks.cancel();
	}, [filters, sortBy, sortOrder, page, pageSize]);

	return (
		<section className="bg-tg-bg py-6 lg:py-10 min-h-screen">
			<div className="container mx-auto">
				{/* Header: stacks on mobile */}
				<div className="mb-4 bg-white rounded-lg p-4">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
						<div>
							<h3 className="text-2xl font-semibold text-dark">Task Board</h3>
							<p className="text-sm text-slate-500">Overview of tasks and status</p>
						</div>

						<div className="flex flex-col sm:flex-row sm:items-center gap-3">
							<div className="flex items-center gap-2">
								<label className="text-sm text-slate-500 mr-2">Sort By:</label>
								<div className="inline-flex items-center rounded-md border border-stroke px-2 py-1 bg-white">
									<select
										id="header-select"
										value={sortBy}
										onChange={handleSortByChange}
										className="appearance-none py-1 pl-1 pr-6 text-sm text-dark outline-none"
									>
										{headers
											.filter((header) => header.sortable)
											.map((header, index) => (
												<option key={index} value={header.value}>
													{header.name}
												</option>
											))}
									</select>
								</div>
								<button
									onClick={toggleSortOrder}
									className="ml-2 rounded-md bg-primary px-3 py-1 text-sm font-medium text-white hover:bg-blue-dark transition"
								>
									{sortOrder === "asc" ? "⬆" : "⬇"}
								</button>
							</div>

							<div className="flex gap-2 items-center">
								{/* Mobile filter toggle - visible only on small screens */}
								<button
									onClick={() => setShowMobileFilters(s => !s)}
									// className="inline-flex items-center gap-2 rounded-md border border-stroke bg-white px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50 transition sm:hidden"
									className={`inline-flex items-center gap-2 rounded-md px-3 py-1 text-sm font-medium transition sm:hidden ${filtersApplied
										? "bg-teal text-white hover:bg-teal-dark"
										: "bg-white text-slate-700 border border-stroke hover:bg-slate-50"
										}`}
									aria-expanded={showMobileFilters}
									aria-controls="mobile-filter-panel"
								>
									{showMobileFilters ? "Hide Filters" : "Show Filters"}
								</button>

								<button
									onClick={resetFilters}
									className="rounded-md bg-white border border-stroke px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
								>
									Reset
								</button>
								<button
									onClick={createTaskHandler}
									className="rounded-md bg-primary px-3 py-1 text-sm font-medium text-white hover:bg-blue-dark transition"
								>
									Create Task
								</button>
							</div>
						</div>
					</div>
				</div>

				{/* Content area */}
				<div className="-mx-4">
					<div className="px-4">
						{/* Mobile cards (visible on small only) */}
						<div className="block sm:hidden space-y-3">
							{/* Mobile filter panel */}
							{showMobileFilters && (
								<div className="bg-white rounded-lg p-3 shadow-sm space-y-3">
									<div>
										<label className="text-xs text-slate-500 block mb-1">Task</label>
										<input
											type="text"
											name="taskName"
											value={filters.taskName}
											onChange={handleFilterChange}
											placeholder="Search Task..."
											className="w-full rounded-sm border border-stroke px-3 py-2 text-sm outline-none"
										/>
									</div>

									<div>
										<label className="text-xs text-slate-500 block mb-1">Assigned By</label>
										<input
											type="text"
											name="assignedByUser"
											value={filters.assignedByUser}
											onChange={handleFilterChange}
											placeholder="Assigned By..."
											className="w-full rounded-sm border border-stroke px-3 py-2 text-sm outline-none"
										/>
									</div>

									<div>
										<label className="text-xs text-slate-500 block mb-1">Assignee</label>
										<input
											type="text"
											name="assignedToUser"
											value={filters.assignedToUser}
											onChange={handleFilterChange}
											placeholder="Assignee..."
											className="w-full rounded-sm border border-stroke px-3 py-2 text-sm outline-none"
										/>
									</div>

									<div>
										<label className="text-xs text-slate-500 block mb-1">Due date</label>
										<input
											type="date"
											name="dueDate"
											value={filters.dueDate}
											onChange={handleFilterChange}
											className="w-full rounded-sm border border-stroke px-3 py-2 text-sm outline-none"
										/>
									</div>

									<div>
										<label className="text-xs text-slate-500 block mb-1">Status</label>
										<select
											name="status"
											value={filters.status}
											onChange={handleFilterChange}
											className="w-full rounded-sm border border-stroke px-3 py-2 text-sm outline-none"
										>
											{statuses.map((status, index) => (
												<option key={index} value={status.value}>
													{status.name}
												</option>
											))}
										</select>
									</div>

									<div>
										<label className="text-xs text-slate-500 block mb-1">Quick Filter</label>
										<select
											name="quickFilter"
											value={filters.quickFilter}
											onChange={(e) => {
												const value = e.target.value;
												if (value === "assignedByMe") {
													handleFilterChange({
														quickFilter: value,
														assignedByUser: loggedInUserName,
														assignedToUser: "",
													});
												} else if (value === "assignedToMe") {
													handleFilterChange({
														quickFilter: value,
														assignedByUser: "",
														assignedToUser: loggedInUserName,
													});
												} else {
													handleFilterChange({
														quickFilter: "",
														assignedByUser: "",
														assignedToUser: "",
													});
												}
											}}
											className="w-full rounded-sm border border-stroke px-3 py-2 text-sm outline-none"
										>
											<option value="">Quick Filter...</option>
											<option value="assignedByMe">Assigned By Me</option>
											<option value="assignedToMe">Assigned To Me</option>
										</select>
									</div>

									<div className="flex gap-2">
										<button
											type="button"
											onClick={() => {
												resetFilters();
											}}
											className="flex-1 rounded-md border border-stroke px-3 py-2 text-sm font-medium text-slate-700 bg-white hover:bg-slate-50"
										>
											Reset
										</button>
										<button
											type="button"
											onClick={() => setShowMobileFilters(false)}
											className="flex-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white"
										>
											Apply
										</button>
									</div>
								</div>
							)}

							{tasks.length === 0 && <div className="text-sm text-slate-500">No tasks found</div>}
							{tasks.map((row) => (
								<MobileTaskCard
									key={row.id}
									row={row}
									viewTaskHandler={viewTaskHandler}
									editTaskHandler={editTaskHandler}
									deleteTaskHandler={deleteTaskHandler}
									loggedInUserId={loggedInUserId}
									isLoggedInUserSuperAdmin = {isLoggedInUserSuperAdmin}
								/>
							))}
						</div>

						{/* Table - visible on sm+ */}
						<div className="hidden sm:block">
							<div className="max-w-full overflow-x-auto rounded-[10px] bg-white shadow-[0px_3px_8px_0px_rgba(0,0,0,0.08)]">
								<table className="w-full table-auto">
									<TableHead
										headers={headers}
										filters={filters}
										statuses={statuses}
										handleFilterChange={handleFilterChange}
										resetFilters={resetFilters}
										createTaskHandler={createTaskHandler}
										taskModal={taskModal}
										onTaskCancel={onTaskCancel}
										onTaskRefresh={onTaskRefresh}
										loggedInUserName={loggedInUserName}
									/>
									<TableBody
										data={tasks}
										confirmModal={confirmModal}
										confirmDelete={confirmDelete}
										cancelDelete={cancelDelete}
										deleteTaskHandler={deleteTaskHandler}
										viewTaskHandler={viewTaskHandler}
										editTaskHandler={editTaskHandler}
										loggedInUserId={loggedInUserId}
										isLoggedInUserSuperAdmin = {isLoggedInUserSuperAdmin}
									/>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div >
			<TaskModal
				isOpen={taskModal.isTaskModalOpen}
				headerText={taskModal.headerText}
				mode={taskModal.mode}
				data={taskModal.data}
				statuses={taskModal.taskStatuses}
				onCancel={onTaskCancel}
				onRefresh={onTaskRefresh}
			/>
			<div className="container mx-auto mt-4 px-4">
				<Pagination
					page={page}
					pageSize={pageSize}
					pageSizeOptions={pageSizeOptions}
					totalPages={totalPages}
					onPageChange={handlePageChange}
					onPageSizeChange={handlePageSizeChange}
				/>
			</div>
		</section >
	);
};

export default TaskBoard;

const TableHead = ({
	headers,
	filters,
	statuses,
	handleFilterChange,
	resetFilters,
	createTaskHandler,
	taskModal,
	onTaskCancel,
	onTaskRefresh,
	loggedInUserName,
}) => {
	return (
		<thead className="h-[110px] bg-[#F9FAFB]">
			<tr className="align-middle">
				<th className="min-w-[180px] px-4 text-left text-[15px] font-large leading-[26px] text-[#111928]">
					{headers[0].name}
				</th>
				<th className="min-w-[180px] px-4 text-left text-[15px] font-large leading-[26px] text-[#111928]">
					{headers[1].name}
				</th>
				<th className="min-w-[180px] px-4 text-left text-[15px] font-large leading-[26px] text-[#111928]">
					{headers[2].name}
				</th>
				<th className="min-w-[180px] px-4 text-left text-[15px] font-large leading-[26px] text-[#111928]">
					{headers[3].name}
				</th>
				<th className="min-w-[180px] px-4 text-left text-[15px] font-large leading-[26px] text-[#111928]">
					{headers[4].name}
				</th>
			</tr>
			<tr className="bg-[#F9FAFB] align-top">
				<th className="px-4 text-left">
					<DefaultColumn>
						<input
							type="text"
							name="taskName"
							value={filters.taskName}
							onChange={handleFilterChange}
							placeholder="Search Task..."
							className="h-[34px] w-full rounded-sm border border-stroke bg-white px-3 text-sm outline-none"
						/>
					</DefaultColumn>
				</th>
				<th className="px-4 text-left">
					<DefaultColumn>
						<input
							type="text"
							name="assignedByUser"
							value={filters.assignedByUser}
							onChange={handleFilterChange}
							placeholder="Search Assigned By..."
							className="h-[34px] w-full rounded-sm border border-stroke bg-white px-3 text-sm outline-none"
						/>
					</DefaultColumn>
				</th>
				<th className="px-4 text-left">
					<DefaultColumn>
						<input
							type="text"
							name="assignedToUser"
							value={filters.assignedToUser}
							onChange={handleFilterChange}
							placeholder="Search Assignee..."
							className="h-[34px] w-full rounded-sm border border-stroke bg-white px-3 text-sm outline-none"
						/>
					</DefaultColumn>
				</th>
				<th className="px-4 text-left">
					<DefaultColumn>
						<input
							type="date"
							name="dueDate"
							value={filters.dueDate}
							onChange={handleFilterChange}
							className="h-[34px] w-full rounded-sm border border-stroke bg-white px-3 text-sm outline-none"
						/>
					</DefaultColumn>
				</th>
				<th className="px-4 text-left">
					<DefaultColumn>
						<select
							name="status"
							value={filters.status}
							onChange={handleFilterChange}
							className="h-[34px] w-full rounded-sm border border-stroke bg-white px-3 text-sm outline-none"
						>
							{statuses.map((status, index) => (
								<option key={index} value={status.value}>
									{status.name}
								</option>
							))}
						</select>
					</DefaultColumn>
				</th>
				<th className="px-4 text-left">
					<DefaultColumn>
						<select
							name="quickFilter"
							value={filters.quickFilter}
							onChange={(e) => {
								const value = e.target.value;
								if (value === "assignedByMe") {
									handleFilterChange({
										quickFilter: value,
										assignedByUser: loggedInUserName,
										assignedToUser: "",
									});
								} else if (value === "assignedToMe") {
									handleFilterChange({
										quickFilter: value,
										assignedByUser: "",
										assignedToUser: loggedInUserName,
									});
								} else {
									handleFilterChange({
										quickFilter: "",
										assignedByUser: "",
										assignedToUser: "",
									});
								}
							}}
							className="h-[34px] w-full mt-1 rounded-sm border border-stroke bg-white px-3 text-sm outline-none"
						>
							<option value="">Quick Filter...</option>
							<option value="assignedByMe">Assigned By Me</option>
							<option value="assignedToMe">Assigned To Me</option>
						</select>
					</DefaultColumn>
				</th>
			</tr>
		</thead>
	);
};

const formatDate = (dateString) => {
	if (!dateString) return "";
	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return "";
	return new Intl.DateTimeFormat("en-GB").format(date); // dd/mm/yyyy
};

const TableBody = ({
	data,
	confirmModal,
	confirmDelete,
	cancelDelete,
	deleteTaskHandler,
	viewTaskHandler,
	editTaskHandler,
	loggedInUserId,
	isLoggedInUserSuperAdmin,
}) => {
	return (
		<tbody>
			{data.map((row, index) => (
				<tr key={index}>
					<td className="border-t border-stroke p-4 pl-5">
						<div className="flex items-center">
							<div>
								<h5 className="text-sm font-medium text-dark">{row.name}</h5>
								{/* <p className="w-44 truncate text-sm text-body-color">
                  {row.description}
                </p> */}
							</div>
						</div>
					</td>
					<td className="border-t border-stroke p-4">
						<p className="text-sm font-medium text-dark">
							{row.assignedByFirstName} {row.assignedByLastName}
						</p>
					</td>
					<td className="border-t border-stroke p-4">
						<p className="text-sm font-medium text-dark">
							{row.firstName} {row.lastName}
						</p>
					</td>
					<td className="border-t border-stroke p-4">
						<p className="text-sm font-medium text-dark">
							{row.dueDate ? formatDate(row.dueDate) : ""}
						</p>
					</td>
					<td className="border-t border-stroke p-4">
						<StatusBadge status={row.status} />
					</td>
					<td className="border-t border-stroke p-4 pr-5 text-center">
						<div className="flex gap-4">
							<button
								onClick={() => viewTaskHandler(row.id)}
								className="relative group flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 hover:border-primary hover:bg-primary hover:text-white"
							>
								<Eye size={18} aria-hidden="true" className="stroke-current" />
								<Tooltip text="View" />
							</button>							
							{(row.assignedByUser == loggedInUserId || row.status !== "COMPLETED" || isLoggedInUserSuperAdmin) && (												
								<button
									onClick={() => editTaskHandler(row.id)}
									className="relative group flex h-8 w-8 items-center justify-center	rounded-md border border-slate-300 hover:border-primary hover:bg-primary hover:text-white"
								>
									<Edit size={18} strokeWidth={1.5} className="stroke-current" />
									<Tooltip text="Edit" />
								</button>
							)}
							{(row.assignedByUser == loggedInUserId || isLoggedInUserSuperAdmin) && (
								<button
									onClick={() => deleteTaskHandler(row.id)}
									className="relative group flex h-8 w-8 items-center justify-center	rounded-md border border-slate-300 hover:border-primary hover:bg-primary hover:text-white"
								>
									<Trash2 size={18} strokeWidth={1.5} className="stroke-current" />
									<Tooltip text="Delete" />
								</button>
							)}
							<ConfirmModal
								isOpen={confirmModal.isOpen}
								onConfirm={confirmDelete}
								onCancel={cancelDelete}
								headerText={confirmModal.headerText}
								bodyText={confirmModal.bodyText}
								bodySubText={confirmModal.bodySubText}
								btnConfirmText={confirmModal.btnConfirmText}
								btnCancelText={confirmModal.btnCancelText}
							/>
						</div>
					</td>
				</tr>
			))}
		</tbody>
	);
};

const DefaultColumn = ({ children }) => {
	return <div className="relative min-w-0">{children}</div>;
};

// common status badge used by table and mobile card
const StatusBadge = ({ status }) => {
	if (!status) return null;

	const map = {
		UNASSIGNED: { cls: "bg-gray-800 text-white", label: "Unassigned" },
		ASSIGNED: { cls: "bg-teal-dark text-white", label: "Assigned" },
		IN_PROGRESS: { cls: "bg-primary text-white", label: "In Progress" },
		BLOCKED: { cls: "bg-red-dark text-white", label: "Blocked" },
		READY_FOR_REVIEW: { cls: "bg-pink-700 text-white", label: "Ready for review" },
		COMPLETED: { cls: "bg-green-dark text-white", label: "Completed" },
		OVERDUE: { cls: "bg-yellow-dark text-white", label: "Overdue" },
		CANCELLED: { cls: "bg-gray-400 text-white", label: "Cancelled" },
	};

	const entry = map[status] || { cls: "bg-gray-200 text-slate-800", label: status.replaceAll("_", " ") };

	return (
		<span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${entry.cls}`}>
			{entry.label}
		</span>
	);
};

const MobileTaskCard = ({ row, viewTaskHandler, editTaskHandler, deleteTaskHandler, loggedInUserId, isLoggedInUserSuperAdmin }) => {
	return (
		<div className="bg-white rounded-lg p-4 shadow-sm">
			{/* Row 1: title (left) + status (right) */}
			<div className="flex items-start justify-between gap-1">
				<h5 className="text-sm font-medium text-dark truncate min-w-0">{row.name}</h5>
				<StatusBadge status={row.status} />

				{/* {row.status && (
					<span className="inline-flex ml-2 shrink-0 rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-white whitespace-nowrap">
						{row.status.replaceAll("_", " ")}
					</span>
				)} */}
			</div>

			{/* Row 2: assignee */}
			<div className="mt-2 text-xs text-slate-500 truncate">
				Assignee: {row.firstName} {row.lastName}
			</div>
			{/* Row 3: assigned by */}
			<div className="mt-1 text-xs text-slate-500 truncate">
				Assigned by: {row.assignedByFirstName} {row.assignedByLastName}
			</div>
			{/* Row 4: due (left) + actions (right) */}
			<div className="mt-3 flex items-center justify-between">
				<div className="text-xs text-slate-500">Due Date: {row.dueDate ? formatDate(row.dueDate) : "—"}</div>

				<div className="flex items-center gap-2">
					<button
						onClick={() => viewTaskHandler(row.id)}
						aria-label="View task"
						className="flex h-8 w-8 items-center justify-center rounded-md border border-stroke text-body-color hover:bg-primary hover:text-white transition"
						title="View"
					>
						<Eye size={18} aria-hidden="true" className="stroke-current" />
					</button>
					{(row.assignedByUser == loggedInUserId || row.status !== "COMPLETED" || isLoggedInUserSuperAdmin) && (			
						<button
							onClick={() => editTaskHandler(row.id)}
							aria-label="Edit task"
							className="flex h-8 w-8 items-center justify-center rounded-md border border-stroke text-body-color hover:bg-primary hover:text-white transition"
							title="Edit"
						>
							<Edit size={18} strokeWidth={1.5} className="stroke-current" />
						</button>
					)}
					{(row.assignedByUser == loggedInUserId || isLoggedInUserSuperAdmin) && (
						<button
							onClick={() => deleteTaskHandler(row.id)}
							aria-label="Delete task"
							className="flex h-8 w-8 items-center justify-center rounded-md border border-stroke text-red-600 hover:bg-primary hover:text-white transition"
							title="Delete"
						>
							<Trash2 size={18} strokeWidth={1.5} className="stroke-current" />
						</button>
					)}
				</div>
			</div>
		</div>
	);
};
